import { useEffect, useState, useRef, useMemo, useCallback, memo } from 'react';
import {
  Box,
  Container,
  Grid,
  Heading,
  Text,
  VStack,
  useColorModeValue,
  Button,
  Icon,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  HStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  useToast,
  Spinner,
  List,
  ListItem,
  ListIcon,
  Skeleton,
  SkeletonText,
  Input,
  Select,
  InputGroup,
  InputLeftElement,
  IconButton,
  Checkbox,
  Progress,
} from '@chakra-ui/react';
import { FiUsers, FiFileText, FiLogOut, FiAlertCircle, FiClock, FiDownload, FiCalendar, FiEdit3, FiAlertTriangle, FiSearch, FiFilter, FiChevronLeft, FiChevronRight, FiX, FiTrash2 } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { useNavigate, Link } from 'react-router-dom';
import { userService, obtenerExpedientesFiltrados, obtenerConteoExpedientes, eliminarExpediente, Expediente, type FiltrosExpedientes } from '../config/index';
import { useAuth } from '../components/AuthProvider';
import { obtenerExpedientesRecientes, obtenerEstadisticasDashboard, type EstadisticasDashboard } from '../config/index';
import type { ExpedientesRecientes } from '../services/estadisticasService';
import { cacheService } from '../services/cacheService';
import { formatearFechaCorta, formatearFechaCompleta } from '../utils/dateUtils';

// Hook personalizado para cargar datos de forma lazy
const useAsyncData = <T,>(
  asyncFunction: () => Promise<T>,
  dependencies: unknown[] = [],
  cacheKey?: string
) => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Verificar caché si se proporciona una clave
      if (cacheKey) {
        const cachedData = cacheService.get<T>(cacheKey);
        if (cachedData) {
          setData(cachedData);
          setLoading(false);
          return;
        }
      }

      const result = await asyncFunction();
      setData(result);

      // Guardar en caché si se proporciona una clave
      if (cacheKey) {
        cacheService.set(cacheKey, result, 2 * 60 * 1000); // 2 minutos
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, dependencies);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return { data, loading, error, refetch: loadData };
};

// Componente optimizado para mostrar expedientes recientes (memoizado)
const EstadisticasExpedientesRecientes: React.FC<{
  data: ExpedientesRecientes | null;
  loading: boolean;
  error: string | null;
  onVerTodosClick?: () => void;
}> = memo(({ data, loading, error, onVerTodosClick }) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <VStack spacing={4} align="stretch">
        <Skeleton height="20px" />
        <SkeletonText mt="4" noOfLines={4} spacing="4" />
      </VStack>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" py={6} color="red.500">
        <Icon as={FiAlertTriangle} boxSize={8} />
        <Text mt={2} fontSize="sm">{error}</Text>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box textAlign="center" py={6}>
        <Text fontSize="sm">No hay datos disponibles</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <Box>
        <HStack mb={2}>
          <Icon as={FiCalendar} color="green.500" />
          <Text fontWeight="medium" fontSize="sm">Creados últimos 7 días</Text>
          <Badge colorScheme="green" borderRadius="full">{data.creadosUltimos7Dias.length}</Badge>
        </HStack>
        
        {data.creadosUltimos7Dias.length > 0 ? (
          <List spacing={1}>
            {data.creadosUltimos7Dias.slice(0, 3).map(exp => (
              <ListItem 
                key={exp.id} 
                p={2} 
                bg="green.50" 
                borderRadius="md"
                cursor="pointer"
                onClick={() => navigate(`/direct-expediente/${exp.id}`)}
                _hover={{ bg: "green.100" }}
                fontSize="sm"
              >
                <HStack justify="space-between">
                  <HStack>
                    <ListIcon as={FiFileText} color="green.500" boxSize={3} />
                    <Text fontWeight="medium">{exp.id}</Text>
                  </HStack>
                  <Text fontSize="xs">{formatearFechaCompleta(exp.fechaCreacion)}</Text>
                </HStack>
              </ListItem>
            ))}
            {data.creadosUltimos7Dias.length > 3 && (
              <Text fontSize="xs" color="gray.500" textAlign="center" mt={1}>
                Y {data.creadosUltimos7Dias.length - 3} más...
              </Text>
            )}
          </List>
        ) : (
          <Text fontSize="xs" color="gray.500">No hay expedientes recientes</Text>
        )}
      </Box>
      
      <Box>
        <HStack mb={2}>
          <Icon as={FiEdit3} color="blue.500" />
          <Text fontWeight="medium" fontSize="sm">Modificados últimos 7 días</Text>
          <Badge colorScheme="blue" borderRadius="full">{data.modificadosUltimos7Dias.length}</Badge>
        </HStack>
        
        {data.modificadosUltimos7Dias.length > 0 ? (
          <List spacing={1}>
            {data.modificadosUltimos7Dias.slice(0, 3).map(exp => (
              <ListItem 
                key={exp.id} 
                p={2} 
                bg="blue.50" 
                borderRadius="md"
                cursor="pointer"
                onClick={() => navigate(`/direct-expediente/${exp.id}`)}
                _hover={{ bg: "blue.100" }}
                fontSize="sm"
              >
                <HStack justify="space-between">
                  <HStack>
                    <ListIcon as={FiFileText} color="blue.500" boxSize={3} />
                    <Text fontWeight="medium">{exp.id}</Text>
                  </HStack>
                  <Text fontSize="xs">
                    {exp.historial && exp.historial.length > 0 
                      ? formatearFechaCompleta(exp.historial[exp.historial.length - 1].fecha)
                      : formatearFechaCompleta(exp.fechaCreacion)}
                  </Text>
                </HStack>
              </ListItem>
            ))}
            {data.modificadosUltimos7Dias.length > 3 && (
              <Text fontSize="xs" color="gray.500" textAlign="center" mt={1}>
                Y {data.modificadosUltimos7Dias.length - 3} más...
              </Text>
            )}
          </List>
        ) : (
          <Text fontSize="xs" color="gray.500">No hay expedientes modificados</Text>
        )}
      </Box>
      
      <Button 
        size="xs" 
        colorScheme="red" 
        variant="outline" 
        alignSelf="center"
        leftIcon={<FiFileText />}
        onClick={onVerTodosClick}
      >
        Ver todos
      </Button>
    </VStack>
  );
});

// Componente optimizado para expedientes sin actividad (memoizado)
const EstadisticasExpedientesSinActividad: React.FC<{
  onVerTodosClick: () => void;
  data: ExpedientesRecientes | null;
  loading: boolean;
  error: string | null;
}> = memo(({ onVerTodosClick, data, loading, error }) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <VStack spacing={4} align="stretch">
        <Skeleton height="20px" />
        <SkeletonText mt="4" noOfLines={4} spacing="4" />
      </VStack>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" py={6} color="red.500">
        <Icon as={FiAlertTriangle} boxSize={8} />
        <Text mt={2} fontSize="sm">{error}</Text>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box textAlign="center" py={6}>
        <Text fontSize="sm">No hay datos disponibles</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <Box>
        <HStack mb={2}>
          <Icon as={FiClock} color="orange.500" />
          <Text fontWeight="medium" fontSize="sm">Sin cambios 7 días</Text>
          <Badge colorScheme="orange" borderRadius="full">{data.sinCambios7Dias.length}</Badge>
        </HStack>
        
        {data.sinCambios7Dias.length > 0 ? (
          <List spacing={1}>
            {data.sinCambios7Dias.slice(0, 3).map(exp => (
              <ListItem 
                key={exp.id} 
                p={2} 
                bg="orange.50" 
                borderRadius="md"
                cursor="pointer"
                onClick={() => navigate(`/direct-expediente/${exp.id}`)}
                _hover={{ bg: "orange.100" }}
                fontSize="sm"
              >
                <HStack justify="space-between">
                  <HStack>
                    <ListIcon as={FiFileText} color="orange.500" boxSize={3} />
                    <Text fontWeight="medium">{exp.id}</Text>
                  </HStack>
                  <Text fontSize="xs">
                    {exp.historial && exp.historial.length > 0 
                      ? formatearFechaCompleta(exp.historial[exp.historial.length - 1].fecha)
                      : formatearFechaCompleta(exp.fechaCreacion)}
                  </Text>
                </HStack>
              </ListItem>
            ))}
            {data.sinCambios7Dias.length > 3 && (
              <Text fontSize="xs" color="gray.500" textAlign="center" mt={1}>
                Y {data.sinCambios7Dias.length - 3} más...
              </Text>
            )}
          </List>
        ) : (
          <Text fontSize="xs" color="gray.500">No hay expedientes sin actividad</Text>
        )}
      </Box>
      
      {data.sinCambios30Dias.length > 0 && (
        <Box 
          p={3} 
          bg="red.100" 
          borderRadius="lg" 
          borderWidth="1px" 
          borderColor="red.300"
        >
          <HStack mb={2} justify="space-between">
            <HStack>
              <Icon as={FiAlertCircle} color="red.600" boxSize={4} />
              <Text fontWeight="bold" color="red.700" fontSize="sm">Sin cambios 30 días</Text>
            </HStack>
            <Badge colorScheme="red" borderRadius="full" fontSize="xs">
              {data.sinCambios30Dias.length}
            </Badge>
          </HStack>
          
          <List spacing={1}>
            {data.sinCambios30Dias.slice(0, 2).map(exp => (
              <ListItem 
                key={exp.id} 
                p={2} 
                bg="white" 
                borderRadius="md"
                cursor="pointer"
                borderLeftWidth="3px"
                borderLeftColor="red.500"
                onClick={() => navigate(`/direct-expediente/${exp.id}`)}
                _hover={{ bg: "red.50" }}
                fontSize="sm"
              >
                <HStack justify="space-between">
                  <HStack>
                    <ListIcon as={FiAlertCircle} color="red.500" boxSize={3} />
                    <Text fontWeight="bold" color="red.700">{exp.id}</Text>
                  </HStack>
                  <Text fontSize="xs" fontWeight="medium">
                    {exp.historial && exp.historial.length > 0 
                      ? formatearFechaCompleta(exp.historial[exp.historial.length - 1].fecha)
                      : formatearFechaCompleta(exp.fechaCreacion)}
                  </Text>
                </HStack>
              </ListItem>
            ))}
            {data.sinCambios30Dias.length > 2 && (
              <Text fontSize="xs" color="red.700" textAlign="center" mt={1} fontWeight="medium">
                ¡{data.sinCambios30Dias.length - 2} expedientes más sin actividad!
              </Text>
            )}
          </List>
          
          <Button 
            mt={2}
            size="sm" 
            colorScheme="red" 
            width="100%"
            leftIcon={<FiAlertCircle />}
            onClick={onVerTodosClick}
          >
            Ver todos sin actividad
          </Button>
        </Box>
      )}
    </VStack>
  );
});

// Componente optimizado para gráfica (memoizado)
const GraficaExpedientes: React.FC<{
  data: ExpedientesRecientes | null;
  loading: boolean;
  error: string | null;
}> = memo(({ data, loading, error }) => {
  const brandRed = useColorModeValue('red.500', 'red.300');

  const chartData = useMemo(() => {
    if (!data) return [];
    return [
      { name: 'Creados (7d)', value: data.creadosUltimos7Dias.length, color: 'green.400' },
      { name: 'Modificados (7d)', value: data.modificadosUltimos7Dias.length, color: 'blue.400' },
      { name: 'Sin cambios (7d)', value: data.sinCambios7Dias.length, color: 'orange.400' },
      { name: 'Sin cambios (30d)', value: data.sinCambios30Dias.length, color: 'red.400' }
    ];
  }, [data]);

  const maxValue = useMemo(() => {
    if (chartData.length === 0) return 0;
    return Math.max(...chartData.map(item => item.value));
  }, [chartData]);
  
  const scale = maxValue > 0 ? 100 / maxValue : 1;

  if (loading) {
    return (
      <VStack spacing={4} align="stretch">
        <Skeleton height="20px" />
        <SkeletonText mt="4" noOfLines={6} spacing="4" />
      </VStack>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" py={6} color="red.500">
        <Icon as={FiAlertTriangle} boxSize={8} />
        <Text mt={2} fontSize="sm">{error}</Text>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box textAlign="center" py={6}>
        <Text fontSize="sm">No hay datos disponibles</Text>
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Heading size="md" mb={4} color={brandRed}>Actividad de Expedientes</Heading>
      
      <VStack spacing={3} align="stretch">
        {chartData.map((item, index) => (
          <Box key={index}>
            <HStack mb={1} justify="space-between">
              <Text fontSize="sm" fontWeight="medium">{item.name}</Text>
              <Text fontSize="sm" fontWeight="bold">{item.value}</Text>
            </HStack>
            <Box position="relative" h="20px">
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bg="gray.100"
                borderRadius="full"
              />
              <Box
                position="absolute"
                top={0}
                left={0}
                width={`${item.value * scale}%`}
                bottom={0}
                bg={item.color}
                borderRadius="full"
                transition="width 1s ease-in-out"
              />
            </Box>
          </Box>
        ))}
      </VStack>
      
      <Box mt={4} p={3} bg="gray.50" borderRadius="md">
        <Text fontSize="sm" fontWeight="medium" textAlign="center">
          Total: {chartData.reduce((sum, item) => sum + item.value, 0)} expedientes
        </Text>
      </Box>
    </Box>
  );
});

export const Dashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.700');
  const brandRed = useColorModeValue('red.500', 'red.300');

  // Estados básicos
  const [activeTab, setActiveTab] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeUsers, setActiveUsers] = useState<number>(0);
  const [conteoExpedientes, setConteoExpedientes] = useState<number>(0);

  // Estados para expedientes (solo se cargan cuando es necesario)
  const [expedientes, setExpedientes] = useState<Expediente[]>([]);
  const [filteredExpedientes, setFilteredExpedientes] = useState<Expediente[]>([]);
  const [totalExpedientesFiltrados, setTotalExpedientesFiltrados] = useState<number>(0);
  const [expedientesLoaded, setExpedientesLoaded] = useState(false);
  const [loadingExpedientes, setLoadingExpedientes] = useState(false);
  const [mostrarSinActividad, setMostrarSinActividad] = useState(false);

  // Estados para paginación y filtros
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [filtros, setFiltros] = useState({
    busqueda: '',
    estado: '',
    tipoBarrera: '',
    departamento: '',
    fechaDesde: '',
    fechaHasta: '',
    ultimaModificacionDesde: '',
    ultimaModificacionHasta: '',
    direccion: '',
    comunicacionInterna: '',
    ordenarPor: 'fecha_desc' // fecha_desc, fecha_asc, id_asc, id_desc, ultima_modificacion_desc, ultima_modificacion_asc
  });
  
  // Estado para búsqueda con debounce
  const [busquedaDebounce, setBusquedaDebounce] = useState('');
  const busquedaTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cargandoExpedientesRef = useRef<boolean>(false);
  const ultimosFiltrosRef = useRef<string>('');

  // Estados para estadísticas
  const [estadisticas, setEstadisticas] = useState({
    total: 0,
    enTramite: 0,
    rechazadas: 0,
    resueltas: 0,
    arquitectonicas: 0,
    urbanisticas: 0,
    transporte: 0,
    tiposBarrera: {} as Record<string, number>,
    departamentos: {} as Record<string, number>
  });

  // Estados para diálogos
  const [expedienteToDelete, setExpedienteToDelete] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [exportandoExcel, setExportandoExcel] = useState<boolean>(false);
  const [exportProgressPercent, setExportProgressPercent] = useState<number>(0);
  const [eliminandoExpediente, setEliminandoExpediente] = useState<boolean>(false);
  
  // Estados para selección múltiple
  const [expedientesSeleccionados, setExpedientesSeleccionados] = useState<Set<string>>(new Set());
  const [eliminandoMultiples, setEliminandoMultiples] = useState<boolean>(false);

  // Cargar datos de expedientes recientes (siempre visible)
  const { 
    data: expedientesRecientesData, 
    loading: expedientesRecientesLoading, 
    error: expedientesRecientesError 
  } = useAsyncData(
    obtenerExpedientesRecientes,
    [],
    'expedientes_recientes'
  );

  // Verificar si el usuario es admin
  const isUserAdmin = useCallback(() => {
    if (currentUser && typeof currentUser === 'object') {
      return currentUser.role === 'Admin';
    }
    try {
      const storedUser = localStorage.getItem('currentUser');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        return user.role === 'Admin';
      }
    } catch (error) {
      console.error("Error verificando localStorage:", error);
    }
    return false;
  }, [currentUser]);

  // Cargar datos básicos al montar el componente - OPTIMIZACIÓN: Cargar en paralelo
  useEffect(() => {
    const init = async () => {
      try {
        console.log('⚡ [DASHBOARD] Iniciando carga rápida del dashboard...');
        const startTime = performance.now();
        
        // Establecer admin inmediatamente
        const adminStatus = isUserAdmin();
        setIsAdmin(adminStatus);

        // Cargar datos en paralelo sin bloquear la UI
        Promise.all([
          userService.getActiveUsersCount().catch(err => {
            console.error('Error al cargar usuarios:', err);
            return 0;
          }),
          obtenerConteoExpedientes().catch(err => {
            console.error('Error al cargar conteo:', err);
            return 0;
          }),
          obtenerEstadisticasDashboard().catch(err => {
            console.error('Error al cargar estadísticas:', err);
            return null;
          })
        ]).then(([count, expedientesCount, statsData]) => {
          setActiveUsers(count);
          setConteoExpedientes(expedientesCount);
          
          // Actualizar estadísticas si se obtuvieron correctamente
          if (statsData) {
            setEstadisticas(statsData);
            console.log('✅ Estadísticas cargadas al inicio');
          }
          
          const endTime = performance.now();
          console.log(`⚡ [DASHBOARD] Datos básicos cargados en ${Math.round(endTime - startTime)}ms`);
        });
        
      } catch (error) {
        console.error('Error al cargar datos básicos:', error);
      }
    };
    init();
  }, [currentUser, isUserAdmin]);

  // Cargar expedientes con paginación real desde la base de datos
  const loadExpedientes = useCallback(async (force = false) => {
    // Evitar recargas múltiples simultáneas
    if (cargandoExpedientesRef.current && !force) {
      console.log('⏸️ [OPTIMIZADO] Ya hay una carga en curso, omitiendo...');
      return;
    }
    
    try {
      cargandoExpedientesRef.current = true;
      setLoadingExpedientes(true);
      console.log('📥 [OPTIMIZADO] Cargando expedientes con paginación desde BD...');
      console.log('🔍 [FILTROS] Búsqueda:', busquedaDebounce, '| Estado:', filtros.estado, '| Tipo:', filtros.tipoBarrera);
      const startTime = performance.now();
      
      // OPTIMIZACIÓN: Usar paginación real desde la base de datos
      // Esto carga solo la página actual, no todos los expedientes
      const filtrosActuales: FiltrosExpedientes = {
        busqueda: busquedaDebounce || undefined,
        estado: filtros.estado || undefined,
        tipoBarrera: filtros.tipoBarrera || undefined,
        departamento: filtros.departamento || undefined,
        fechaDesde: filtros.fechaDesde || undefined,
        fechaHasta: filtros.fechaHasta || undefined,
        direccion: filtros.direccion || undefined,
        comunicacionInterna: filtros.comunicacionInterna || undefined,
        ordenarPor: filtros.ordenarPor as 'fecha_desc' | 'fecha_asc' | 'id_asc' | 'id_desc'
      };
      
      const offset = (currentPage - 1) * itemsPerPage;
      const { expedientes: expedientesData, total } = await obtenerExpedientesFiltrados(
        filtrosActuales,
        itemsPerPage,
        offset
      );
      
      console.log(`✅ Cargados ${expedientesData.length} expedientes de ${total} totales (página ${currentPage})`);
      console.log('🔍 [RESULTADO] Filtros aplicados correctamente, mostrando resultados filtrados');
      console.log('🔍 [RESULTADO] Estado de filtros:', {
        busquedaDebounce,
        estado: filtros.estado,
        tipoBarrera: filtros.tipoBarrera,
        departamento: filtros.departamento
      });
      
      // Actualizar siempre el estado con los resultados de la búsqueda
      setExpedientes(expedientesData);
      setFilteredExpedientes(expedientesData);
      setTotalExpedientesFiltrados(total);
      console.log('✅ [RESULTADO] Estado actualizado con', expedientesData.length, 'expedientes filtrados');
      
      // OPTIMIZACIÓN: Usar la función SQL optimizada para estadísticas
      // Solo actualizar estadísticas si no hay filtros activos para evitar recargas innecesarias
      const tieneFiltrosActivos = busquedaDebounce || filtros.estado || filtros.tipoBarrera || filtros.departamento || filtros.fechaDesde || filtros.fechaHasta || filtros.direccion || filtros.comunicacionInterna;
      
      if (!tieneFiltrosActivos) {
        console.log('📊 Cargando estadísticas con SQL optimizado...');
        const statsData = await obtenerEstadisticasDashboard();
        
        if (statsData) {
          setEstadisticas(statsData);
          console.log('✅ Estadísticas cargadas desde SQL');
        } else {
          // Fallback: calcular con los expedientes cargados
          const stats = calcularEstadisticas(expedientesData);
          setEstadisticas(stats);
          console.log('⚠️ Estadísticas calculadas localmente (fallback)');
        }
      } else {
        // Si hay filtros activos, solo calcular estadísticas con los resultados filtrados
        const stats = calcularEstadisticas(expedientesData);
        setEstadisticas(stats);
        console.log('📊 Estadísticas calculadas con resultados filtrados');
      }
      
      const endTime = performance.now();
      console.log(`⚡ [OPTIMIZADO] Expedientes y estadísticas cargados en ${Math.round(endTime - startTime)}ms`);
      
      setExpedientesLoaded(true);
    } catch (error) {
      console.error('Error al cargar expedientes:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los expedientes',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingExpedientes(false);
      cargandoExpedientesRef.current = false;
    }
  }, [busquedaDebounce, filtros.estado, filtros.tipoBarrera, filtros.departamento, filtros.fechaDesde, filtros.fechaHasta, filtros.direccion, filtros.comunicacionInterna, filtros.ordenarPor, currentPage, itemsPerPage, toast]);

  // Calcular estadísticas de forma optimizada
  const calcularEstadisticas = useCallback((expedientes: Expediente[]) => {
    const stats = {
      total: expedientes.length,
      enTramite: 0,
      rechazadas: 0,
      resueltas: 0,
      arquitectonicas: 0,
      urbanisticas: 0,
      transporte: 0,
      tiposBarrera: {} as Record<string, number>,
      departamentos: {} as Record<string, number>
    };

    const tiposEspecificos = [
      "Barreras Urbanísticas",
      "Barreras de la Edificación",
      "Transporte",
      "Barreras de la Comunicación",
      "Ayudas Técnicas y Consultas"
    ];
    
    tiposEspecificos.forEach(tipo => {
      stats.tiposBarrera[tipo] = 0;
    });

    expedientes.forEach(exp => {
      // Contar por estado
      if (exp.estado === 'En trámite') stats.enTramite++;
      else if (exp.estado === 'Rechazada') stats.rechazadas++;
      else if (exp.estado === 'Resuelto') stats.resueltas++;

      // Contar por tipo de barrera
      const tipoBarrera = exp.tipoBarrera ? exp.tipoBarrera.toLowerCase() : '';
      
      if (tipoBarrera.includes('edificación') || tipoBarrera.includes('edificacion')) {
        stats.arquitectonicas++;
      } 
      else if (tipoBarrera.includes('urban')) {
        stats.urbanisticas++;
      }
      else if (tipoBarrera.includes('transporte') || tipoBarrera.includes('transport')) {
        stats.transporte++;
      }
      
      // Contar todos los tipos de barrera
      if (exp.tipoBarrera) {
        if (!stats.tiposBarrera[exp.tipoBarrera]) {
          stats.tiposBarrera[exp.tipoBarrera] = 0;
        }
        stats.tiposBarrera[exp.tipoBarrera]++;
      }
      
      // Contar por departamento
      if (exp.Departamento) {
        if (!stats.departamentos[exp.Departamento]) {
          stats.departamentos[exp.Departamento] = 0;
        }
        stats.departamentos[exp.Departamento]++;
      }
    });

    return stats;
  }, []);

  // Función auxiliar para convertir fecha a objeto Date
  const parsearFecha = useCallback((fechaString: string): Date => {
    try {
      if (fechaString.includes('T')) {
        // Formato ISO (2025-05-06T15:26:59.778Z) - ESTE ES EL FORMATO REAL EN LA BD
        return new Date(fechaString);
      } else if (fechaString.includes(',')) {
        const [fechaParte, horaParte] = fechaString.split(',');
        const [dia, mes, anio] = fechaParte.trim().split('/');
        return new Date(`${anio}-${mes}-${dia}${horaParte}`);
      } else if (fechaString.includes('-') && fechaString.includes('/')) {
        // Formato "DD/MM/YYYY - HH:MM:SS"
        const [fechaParte, horaParte] = fechaString.split('-');
        const [dia, mes, anio] = fechaParte.trim().split('/');
        const [horas, minutos, segundos] = horaParte.trim().split(':').map(Number);
        return new Date(parseInt(anio), parseInt(mes) - 1, parseInt(dia), horas, minutos, segundos);
      } else if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(fechaString)) {
        const [dia, mes, anio] = fechaString.split('/');
        return new Date(`${anio}-${mes}-${dia}`);
      } else {
        return new Date(fechaString);
      }
    } catch (error) {
      console.error('Error al parsear fecha:', error);
      return new Date();
    }
  }, []);

  // Función para obtener la fecha de última modificación de un expediente
  const obtenerFechaUltimaModificacion = useCallback((expediente: Expediente): Date => {
    if (expediente.historial && expediente.historial.length > 0) {
      // Obtener la fecha más reciente del historial
      const fechaUltimaModificacion = expediente.historial[expediente.historial.length - 1].fecha;
      return parsearFecha(fechaUltimaModificacion);
    }
    // Si no hay historial, usar la fecha de creación
    return parsearFecha(expediente.fechaCreacion);
  }, [parsearFecha]);

  // Aplicar filtros de forma optimizada
  const aplicarFiltros = useCallback(() => {
    let resultado = [...expedientes];

    // Filtro por tipo de barrera
    if (filtros.tipoBarrera) {
      resultado = resultado.filter(exp => exp.tipoBarrera === filtros.tipoBarrera);
    }

    // Filtro por estado
    if (filtros.estado) {
      resultado = resultado.filter(exp => exp.estado === filtros.estado);
    }

    // Filtro por departamento
    if (filtros.departamento) {
      resultado = resultado.filter(exp => exp.Departamento === filtros.departamento);
    }

    // Filtro por búsqueda de texto
    if (filtros.busqueda) {
      const busquedaLower = filtros.busqueda.toLowerCase();
      resultado = resultado.filter(exp =>
        exp.id.toLowerCase().includes(busquedaLower) ||
        (exp.nombreApellidos?.toLowerCase().includes(busquedaLower)) ||
        exp.email.toLowerCase().includes(busquedaLower) ||
        exp.descripcion.toLowerCase().includes(busquedaLower)
      );
    }

    // Filtro por dirección
    if (filtros.direccion) {
      const direccionLower = filtros.direccion.toLowerCase();
      resultado = resultado.filter(exp =>
        (exp.direccion?.toLowerCase().includes(direccionLower)) ||
        (exp.localidad?.toLowerCase().includes(direccionLower)) ||
        (exp.codigoPostal?.toLowerCase().includes(direccionLower)) ||
        (exp.provincia?.toLowerCase().includes(direccionLower))
      );
    }

    // Filtro por comunicación interna
    if (filtros.comunicacionInterna) {
      const comunicacionLower = filtros.comunicacionInterna.toLowerCase();
      resultado = resultado.filter(exp =>
        exp.comunicacionesInternas && exp.comunicacionesInternas.some(com =>
          com.mensaje.toLowerCase().includes(comunicacionLower) ||
          com.usuario.toLowerCase().includes(comunicacionLower)
        )
      );
    }

    // Filtro por rango de fechas de creación
    if (filtros.fechaDesde) {
      const fechaDesde = new Date(filtros.fechaDesde);
      fechaDesde.setHours(0, 0, 0, 0);
      resultado = resultado.filter(exp => {
        const fechaExp = parsearFecha(exp.fechaCreacion);
        return fechaExp >= fechaDesde;
      });
    }

    if (filtros.fechaHasta) {
      const fechaHasta = new Date(filtros.fechaHasta);
      fechaHasta.setHours(23, 59, 59, 999);
      resultado = resultado.filter(exp => {
        const fechaExp = parsearFecha(exp.fechaCreacion);
        return fechaExp <= fechaHasta;
      });
    }

    // Filtro por rango de fechas de última modificación
    if (filtros.ultimaModificacionDesde) {
      const fechaDesde = new Date(filtros.ultimaModificacionDesde);
      fechaDesde.setHours(0, 0, 0, 0);
      resultado = resultado.filter(exp => {
        const fechaUltimaModificacion = obtenerFechaUltimaModificacion(exp);
        return fechaUltimaModificacion >= fechaDesde;
      });
    }

    if (filtros.ultimaModificacionHasta) {
      const fechaHasta = new Date(filtros.ultimaModificacionHasta);
      fechaHasta.setHours(23, 59, 59, 999);
      resultado = resultado.filter(exp => {
        const fechaUltimaModificacion = obtenerFechaUltimaModificacion(exp);
        return fechaUltimaModificacion <= fechaHasta;
      });
    }

    // Aplicar ordenamiento
    resultado.sort((a, b) => {
      switch (filtros.ordenarPor) {
        case 'fecha_asc':
          return parsearFecha(a.fechaCreacion).getTime() - parsearFecha(b.fechaCreacion).getTime();
        case 'fecha_desc':
          return parsearFecha(b.fechaCreacion).getTime() - parsearFecha(a.fechaCreacion).getTime();
        case 'id_asc':
          return a.id.localeCompare(b.id);
        case 'id_desc':
          return b.id.localeCompare(a.id);
        default:
          return parsearFecha(b.fechaCreacion).getTime() - parsearFecha(a.fechaCreacion).getTime();
      }
    });

    setFilteredExpedientes(resultado);
    setCurrentPage(1); // Resetear a la primera página cuando se aplican filtros
  }, [expedientes, filtros, parsearFecha]);

  // Efecto para manejar debounce de búsqueda (1 segundo)
  useEffect(() => {
    // Limpiar timeout anterior si existe
    if (busquedaTimeoutRef.current) {
      clearTimeout(busquedaTimeoutRef.current);
    }
    
    // Si el campo de búsqueda está vacío y ya había una búsqueda, no hacer nada
    // Esto evita que se recarguen todos los expedientes cuando se borra la búsqueda
    if (!filtros.busqueda && busquedaDebounce) {
      console.log('⏸️ [DEBOUNCE] Campo de búsqueda vacío pero hay búsqueda previa, esperando...');
      // Solo actualizar si el usuario realmente borró todo (esperar 1 segundo más)
      busquedaTimeoutRef.current = setTimeout(() => {
        console.log('🔄 [DEBOUNCE] Actualizando búsqueda a vacío después de espera');
        setBusquedaDebounce('');
      }, 1000);
      return;
    }
    
    // Crear nuevo timeout para debounce (1000ms = 1 segundo)
    busquedaTimeoutRef.current = setTimeout(() => {
      console.log('✅ [DEBOUNCE] Actualizando búsqueda debounce a:', filtros.busqueda);
      setBusquedaDebounce(filtros.busqueda);
    }, 1000);
    
    // Cleanup
    return () => {
      if (busquedaTimeoutRef.current) {
        clearTimeout(busquedaTimeoutRef.current);
      }
    };
  }, [filtros.busqueda, busquedaDebounce]);

  // Recargar expedientes cuando cambia la página o los filtros (usando busquedaDebounce en lugar de filtros.busqueda)
  useEffect(() => {
    // Solo cargar si los expedientes ya fueron cargados inicialmente o si estamos en la pestaña de denuncias
    const denunciasTabIndex = isAdmin ? 4 : 3;
    const estaEnPestanaDenuncias = activeTab === denunciasTabIndex;
    
    if (!estaEnPestanaDenuncias && !expedientesLoaded) {
      // No cargar si no estamos en la pestaña de denuncias y aún no se han cargado
      return;
    }
    
    // Crear una clave única para los filtros actuales
    const claveFiltros = JSON.stringify({
      busquedaDebounce,
      estado: filtros.estado,
      tipoBarrera: filtros.tipoBarrera,
      departamento: filtros.departamento,
      fechaDesde: filtros.fechaDesde,
      fechaHasta: filtros.fechaHasta,
      direccion: filtros.direccion,
      comunicacionInterna: filtros.comunicacionInterna,
      ordenarPor: filtros.ordenarPor,
      currentPage
    });
    
    // Solo recargar si los filtros realmente han cambiado
    if (ultimosFiltrosRef.current === claveFiltros) {
      console.log('⏸️ [EFFECT] Filtros no han cambiado, omitiendo recarga');
      return;
    }
    
    console.log('🔄 [EFFECT] Filtros han cambiado, recargando expedientes');
    console.log('🔍 [EFFECT] Estado actual - busquedaDebounce:', busquedaDebounce, '| filtros:', filtros);
    
    // Actualizar la referencia de los últimos filtros
    ultimosFiltrosRef.current = claveFiltros;
    
    // Usar un pequeño delay para evitar recargas múltiples rápidas y agrupar cambios
    const timeoutId = setTimeout(() => {
      loadExpedientes();
    }, 150);
    
    return () => {
      clearTimeout(timeoutId);
    };
    // Remover loadExpedientes de las dependencias para evitar recargas innecesarias
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, busquedaDebounce, filtros.estado, filtros.tipoBarrera, filtros.departamento, filtros.fechaDesde, filtros.fechaHasta, filtros.direccion, filtros.comunicacionInterna, filtros.ordenarPor, expedientesLoaded, activeTab, isAdmin]);

  // Limpiar selección cuando cambie de página o se apliquen filtros
  useEffect(() => {
    setExpedientesSeleccionados(new Set());
  }, [currentPage, filteredExpedientes.length]);

  // Calcular datos de paginación usando el total de la BD
  const paginationData = useMemo(() => {
    // Usar el total de expedientes filtrados desde la BD
    const totalItems = totalExpedientesFiltrados || filteredExpedientes.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
    // Los items actuales ya están en filteredExpedientes (no necesitamos slice)
    const currentItems = filteredExpedientes;

    return {
      totalItems,
      totalPages,
      currentItems,
      startIndex: startIndex + 1,
      endIndex
    };
  }, [filteredExpedientes, totalExpedientesFiltrados, currentPage, itemsPerPage]);

  const PAGE_SIZE_EXPORT = 200;
  // Exportar a Excel: todos los expedientes que coinciden con los filtros actuales (paginación)
  const exportarDenunciasExcel = useCallback(async () => {
    try {
      if (totalExpedientesFiltrados === 0) {
        toast({
          title: 'Sin datos',
          description: 'No hay expedientes para exportar.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      setExportandoExcel(true);
      setExportProgressPercent(0);

      const filtrosActuales: FiltrosExpedientes = {
        busqueda: busquedaDebounce || undefined,
        estado: filtros.estado || undefined,
        tipoBarrera: filtros.tipoBarrera || undefined,
        departamento: filtros.departamento || undefined,
        fechaDesde: filtros.fechaDesde || undefined,
        fechaHasta: filtros.fechaHasta || undefined,
        direccion: filtros.direccion || undefined,
        comunicacionInterna: filtros.comunicacionInterna || undefined,
        ordenarPor: filtros.ordenarPor as 'fecha_desc' | 'fecha_asc' | 'id_asc' | 'id_desc'
      };

      const todosExpedientes: Expediente[] = [];
      let offset = 0;
      let total = totalExpedientesFiltrados;

      while (todosExpedientes.length < total) {
        const { expedientes: page, total: totalFromApi } = await obtenerExpedientesFiltrados(
          filtrosActuales,
          PAGE_SIZE_EXPORT,
          offset
        );
        total = totalFromApi;
        todosExpedientes.push(...page);
        setExportProgressPercent(total > 0 ? Math.round((todosExpedientes.length / total) * 100) : 100);
        if (page.length < PAGE_SIZE_EXPORT) break;
        offset += PAGE_SIZE_EXPORT;
      }

      setExportProgressPercent(100);

      const datosExcel = todosExpedientes.map(exp => ({
        'ID Expediente': exp.id,
        'Fecha Creación': exp.fechaCreacion,
        'Estado': exp.estado,
        'Tipo de Barrera': exp.tipoBarrera,
        'Departamento': exp.Departamento,
        'Descripción': exp.descripcion,
        'Email': exp.email,
        'Nombre y Apellidos': exp.nombreApellidos || '',
        'Teléfono Fijo': exp.telefonoFijo || '',
        'Móvil': exp.movil || '',
        'Dirección': exp.direccion || '',
        'Localidad': exp.localidad || '',
        'Código Postal': exp.codigoPostal || '',
        'Provincia': exp.provincia || '',
        'Tiene Foto': exp.imagenUrl || exp.imagenBarrera ? 'Sí' : 'No'
      }));

      const libro = XLSX.utils.book_new();
      const hoja = XLSX.utils.json_to_sheet(datosExcel);
      XLSX.utils.book_append_sheet(libro, hoja, 'Denuncias');
      XLSX.writeFile(libro, `Denuncias_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`);

      toast({
        title: 'Exportación completada',
        description: `Se han exportado ${datosExcel.length} denuncias a Excel.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      toast({
        title: 'Error al exportar',
        description: 'No se pudieron exportar las denuncias a Excel.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExportandoExcel(false);
      setExportProgressPercent(0);
    }
  }, [busquedaDebounce, filtros.estado, filtros.tipoBarrera, filtros.departamento, filtros.fechaDesde, filtros.fechaHasta, filtros.direccion, filtros.comunicacionInterna, filtros.ordenarPor, totalExpedientesFiltrados, toast]);

  // Eliminar expediente
  const handleDeleteExpediente = useCallback(async () => {
    if (expedienteToDelete) {
      try {
        setEliminandoExpediente(true);
        console.log('🗑️ [DASHBOARD] Iniciando eliminación del expediente:', expedienteToDelete);
        
        await eliminarExpediente(expedienteToDelete);
        
        // Forzar recarga de expedientes invalidando el estado
        setExpedientesLoaded(false);
        setExpedientes([]);
        setFilteredExpedientes([]);
        
        // Recargar el conteo de expedientes
        const expedientesCount = await obtenerConteoExpedientes();
        setConteoExpedientes(expedientesCount);
        
        // Recargar datos forzando la recarga
        await loadExpedientes(true);
        
        toast({
          title: "Expediente eliminado",
          description: `El expediente ${expedienteToDelete} ha sido eliminado correctamente.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        console.log('✅ [DASHBOARD] Expediente eliminado y datos recargados');
      } catch (error) {
        console.error('❌ [DASHBOARD] Error al eliminar expediente:', error);
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "No se pudo eliminar el expediente. Inténtalo de nuevo.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setEliminandoExpediente(false);
      }
    }
    setExpedienteToDelete(null);
    onClose();
  }, [expedienteToDelete, loadExpedientes, toast, onClose]);

  // Funciones para selección múltiple
  const toggleSeleccionExpediente = useCallback((expedienteId: string) => {
    setExpedientesSeleccionados(prev => {
      const newSet = new Set(prev);
      if (newSet.has(expedienteId)) {
        newSet.delete(expedienteId);
      } else {
        newSet.add(expedienteId);
      }
      return newSet;
    });
  }, []);

  const seleccionarTodos = useCallback(() => {
    if (expedientesSeleccionados.size === paginationData.currentItems.length) {
      // Si todos están seleccionados, deseleccionar todos
      setExpedientesSeleccionados(new Set());
    } else {
      // Seleccionar todos los expedientes de la página actual
      const todosIds = new Set(paginationData.currentItems.map(exp => exp.id));
      setExpedientesSeleccionados(todosIds);
    }
  }, [expedientesSeleccionados.size, paginationData.currentItems]);

  const limpiarSeleccion = useCallback(() => {
    setExpedientesSeleccionados(new Set());
  }, []);

  // Función para eliminar múltiples expedientes
  const eliminarExpedientesSeleccionados = useCallback(async () => {
    if (expedientesSeleccionados.size === 0) return;

    try {
      setEliminandoMultiples(true);
      console.log('🗑️ [DASHBOARD] Iniciando eliminación múltiple de expedientes:', Array.from(expedientesSeleccionados));
      
      const expedientesArray = Array.from(expedientesSeleccionados);
      let eliminadosExitosos = 0;
      let errores = 0;

      // Eliminar expedientes uno por uno
      for (const expedienteId of expedientesArray) {
        try {
          await eliminarExpediente(expedienteId);
          eliminadosExitosos++;
          console.log(`✅ [DASHBOARD] Expediente ${expedienteId} eliminado exitosamente`);
        } catch (error) {
          errores++;
          console.error(`❌ [DASHBOARD] Error al eliminar expediente ${expedienteId}:`, error);
        }
      }

      // Limpiar selección
      setExpedientesSeleccionados(new Set());
      
      // Forzar recarga de expedientes
      setExpedientesLoaded(false);
      setExpedientes([]);
      setFilteredExpedientes([]);
      
      // Recargar el conteo de expedientes
      const expedientesCount = await obtenerConteoExpedientes();
      setConteoExpedientes(expedientesCount);
      
      // Recargar datos
      await loadExpedientes(true);
      
      // Mostrar resultado
      if (eliminadosExitosos > 0 && errores === 0) {
        toast({
          title: "Expedientes eliminados",
          description: `Se han eliminado ${eliminadosExitosos} expedientes correctamente.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else if (eliminadosExitosos > 0 && errores > 0) {
        toast({
          title: "Eliminación parcial",
          description: `Se eliminaron ${eliminadosExitosos} expedientes. ${errores} expedientes no pudieron ser eliminados.`,
          status: "warning",
          duration: 7000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error en eliminación",
          description: "No se pudieron eliminar los expedientes seleccionados.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
      
      console.log('✅ [DASHBOARD] Eliminación múltiple completada');
    } catch (error) {
      console.error('❌ [DASHBOARD] Error en eliminación múltiple:', error);
      toast({
        title: "Error",
        description: "Ocurrió un error durante la eliminación múltiple.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setEliminandoMultiples(false);
    }
  }, [expedientesSeleccionados, loadExpedientes, toast]);

  // Componente para tarjetas del dashboard
  const DashboardCard = ({ 
    title, 
    description, 
    icon, 
    onClick,
    statNumber,
    statLabel 
  }: { 
    title: string;
    description: string;
    icon: typeof FiUsers;
    onClick: () => void;
    statNumber?: string;
    statLabel?: string;
  }) => (
    <Box
      p={6}
      bg={cardBg}
      borderRadius="lg"
      boxShadow="md"
      cursor="pointer"
      onClick={onClick}
      _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
      transition="all 0.2s"
    >
      <VStack spacing={4} align="flex-start">
        <Icon as={icon} boxSize={8} color={brandRed} />
        <Heading size="md">{title}</Heading>
        <Text color="gray.600">{description}</Text>
        {statNumber && statLabel && (
          <Stat mt={2}>
            <StatNumber color={brandRed}>{statNumber}</StatNumber>
            <StatLabel>{statLabel}</StatLabel>
            <StatHelpText>Última actualización: hoy</StatHelpText>
          </Stat>
        )}
      </VStack>
    </Box>
  );

  return (
    <Box 
      minH="100vh" 
      backgroundImage="url('/silla.jpg')"
      backgroundSize="cover"
      backgroundPosition="center"
      position="relative"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="whiteAlpha.700"
      />
      <Container maxW="container.xl" position="relative" zIndex={1}>
        <VStack spacing={8} align="stretch">
          <Box 
            position="relative"
            bg="whiteAlpha.500"
            p={{ base: 2, md: 3 }}
            borderRadius="xl"
            backdropFilter="blur(10px)"
            display="flex"
            alignItems="center"
            minH={{ base: "60px", md: "70px" }}
          >
            <Link to="/">
              <Box 
                as="img" 
                src="/famma.png" 
                alt="Logo Famma" 
                className="logo" 
                h={{ base: "45px", md: "55px" }}
                objectFit="contain"
                transition="transform 0.2s ease"
                _hover={{ transform: 'scale(1.05)' }}
                ml={{ base: 2, md: 3 }}
              />
            </Link>
            <Box flex="1" textAlign="center">
              <Heading
                fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                fontWeight="bold"
                color="brand.600"
                letterSpacing="tight"
                lineHeight="shorter"
              >
                Panel de Control
              </Heading>
            </Box>
            <Box w={{ base: "45px", md: "55px" }} />
          </Box>

          <Box 
            bg="white" 
            p={6} 
            borderRadius="lg" 
            boxShadow="sm"
            mb={6}
          >
            <Flex justify="space-between" align="center">
              <Box>
                <Heading color={brandRed} mb={2}>Panel de Control</Heading>
                <VStack align="flex-start" spacing={1}>
                  <Text color="gray.600">
                    Bienvenido al panel de {currentUser?.role?.toLowerCase() || 'usuario'}
                  </Text>
                  <Text color="gray.500" fontSize="sm">
                    {currentUser?.email}
                  </Text>
                </VStack>
              </Box>
              <HStack spacing={4}>
                <Button
                  colorScheme="red"
                  variant="outline"
                  onClick={() => navigate('/')}
                >
                  Volver al inicio
                </Button>
                <Button
                  leftIcon={<FiLogOut />}
                  colorScheme="red"
                  variant="ghost"
                  onClick={async () => {
                    try {
                      await userService.logout();
                      navigate('/login');
                    } catch (error) {
                      console.error('Error al cerrar sesión:', error);
                    }
                  }}
                >
                  Cerrar sesión
                </Button>
              </HStack>
            </Flex>
          </Box>

          {/* Contenido principal optimizado */}
          <Tabs 
            variant="soft-rounded" 
            colorScheme="red" 
            size="lg"
            isLazy
            bg="white"
            p={4}
            borderRadius="xl"
            boxShadow="md"
            index={activeTab}
            onChange={(index) => {
              setActiveTab(index);
              // Cargar expedientes solo cuando se necesiten
              // El índice de la pestaña de denuncias depende de si el usuario es admin
              const denunciasTabIndex = isAdmin ? 4 : 3;
              
              // Solo cargar expedientes cuando se accede a la pestaña de Denuncias
              if (index === denunciasTabIndex && !expedientesLoaded) {
                loadExpedientes();
              }
            }}
          >
            <TabList mb={4} gap={2} borderBottom="1px solid" borderColor="gray.200" pb={4} px={2}>
              <Tab _selected={{ color: "white", bg: brandRed, boxShadow: "md" }} fontWeight="medium" px={6} py={3} borderRadius="full">
                <HStack spacing={2}>
                  <Icon as={FiFileText} boxSize={5} />
                  <Text>Inicio</Text>
                </HStack>
              </Tab>
              <Tab _selected={{ color: "white", bg: brandRed, boxShadow: "md" }} fontWeight="medium" px={6} py={3} borderRadius="full">
                <HStack spacing={2}>
                  <Icon as={FiFileText} boxSize={5} />
                  <Text>Últimas actualizaciones</Text>
                  {estadisticas.enTramite > 0 && (
                    <Badge colorScheme="orange" borderRadius="full" px={2} py={1} fontSize="xs" fontWeight="bold" ml={1}>
                      {estadisticas.enTramite}
                    </Badge>
                  )}
                </HStack>
              </Tab>
              {isAdmin && (
                <Tab _selected={{ color: "white", bg: brandRed, boxShadow: "md" }} fontWeight="bold" px={6} py={3} borderRadius="full" bg="blue.50">
                  <HStack spacing={2}>
                    <Icon as={FiUsers} boxSize={5} color="blue.500" />
                    <Text color="blue.700">Administrador</Text>
                    <Badge colorScheme="blue" variant="solid">Admin</Badge>
                  </HStack>
                </Tab>
              )}
              <Tab _selected={{ color: "white", bg: brandRed, boxShadow: "md" }} fontWeight="medium" px={6} py={3} borderRadius="full">
                <HStack spacing={2}>
                  <Icon as={FiFileText} boxSize={5} />
                  <Text>Estadísticas</Text>
                </HStack>
              </Tab>
              <Tab _selected={{ color: "white", bg: brandRed, boxShadow: "md" }} fontWeight="medium" px={6} py={3} borderRadius="full">
                <HStack spacing={2}>
                  <Icon as={FiFileText} boxSize={5} />
                  <Text>Denuncias</Text>
                  <Badge colorScheme="blue" borderRadius="full" px={2} py={1} fontSize="xs" fontWeight="bold" ml={1}>
                    {conteoExpedientes}
                  </Badge>
                </HStack>
              </Tab>
            </TabList>

            <TabPanels>
              {/* Panel de Inicio */}
              <TabPanel p={4}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
                  <Box p={5} bg="gray.50" borderRadius="lg" boxShadow="sm">
                    <Heading size="md" mb={4} color={brandRed}>
                      <HStack>
                        <Icon as={FiClock} />
                        <Text>Expedientes Recientes</Text>
                      </HStack>
                    </Heading>
                    <EstadisticasExpedientesRecientes 
                      data={expedientesRecientesData}
                      loading={expedientesRecientesLoading}
                      error={expedientesRecientesError}
                      onVerTodosClick={() => {
                        const denunciasTabIndex = isAdmin ? 4 : 3;
                        setActiveTab(denunciasTabIndex);
                        if (!expedientesLoaded) {
                          loadExpedientes();
                        }
                      }}
                    />
                  </Box>
                  
                  <Box p={5} bg="gray.50" borderRadius="lg" boxShadow="sm">
                    <Heading size="md" mb={4} color={brandRed}>
                      <HStack>
                        <Icon as={FiAlertCircle} />
                        <Text>Expedientes Sin Actividad</Text>
                      </HStack>
                    </Heading>
                    <EstadisticasExpedientesSinActividad 
                      data={expedientesRecientesData}
                      loading={expedientesRecientesLoading}
                      error={expedientesRecientesError}
                      onVerTodosClick={() => {
                        setMostrarSinActividad(true);
                        const denunciasTabIndex = isAdmin ? 4 : 3;
                        setActiveTab(denunciasTabIndex);
                        if (!expedientesLoaded) {
                          loadExpedientes();
                        }
                      }}
                    />
                  </Box>
                </SimpleGrid>
                
                <Box p={5} bg="gray.50" borderRadius="lg" boxShadow="sm">
                  <GraficaExpedientes 
                    data={expedientesRecientesData}
                    loading={expedientesRecientesLoading}
                    error={expedientesRecientesError}
                  />
                </Box>
              </TabPanel>

              {/* Panel de Últimas Actualizaciones */}
              <TabPanel p={4}>
                <Box textAlign="center" py={10}>
                  <VStack spacing={6}>
                    <Icon as={FiClock} boxSize={16} color={brandRed} />
                    <Heading size="lg" color={brandRed}>
                      Últimas Actualizaciones
                    </Heading>
                    <Text color="gray.600" fontSize="lg" maxW="md" textAlign="center">
                      Consulta todas las actualizaciones recientes de los expedientes, 
                      ordenadas por fecha con información detallada de cada cambio.
                    </Text>
                    <Button 
                      size="lg" 
                      colorScheme="red" 
                      leftIcon={<FiClock />}
                      onClick={() => navigate('/ultimas-actualizaciones')}
                      boxShadow="md"
                      _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                    >
                      Ver Últimas Actualizaciones
                    </Button>
                  </VStack>
                </Box>
              </TabPanel>

              {/* Panel de Administrador */}
              {isAdmin && (
                <TabPanel p={4}>
                  <VStack spacing={6} align="stretch">
                    <Heading size="lg" color={brandRed} mb={4}>
                      <Icon as={FiUsers} mr={2} />
                      Panel de Administración
                    </Heading>
                    
                    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                      <DashboardCard
                        title="Gestión de Usuarios"
                        description="Crear y administrar usuarios del sistema"
                        icon={FiUsers}
                        onClick={() => navigate('/admin/users')}
                        statNumber={activeUsers.toString()}
                        statLabel="Usuarios activos"
                      />
                      
                      <Box p={6} bg={cardBg} borderRadius="lg" boxShadow="md">
                        <VStack spacing={4} align="flex-start">
                          <HStack>
                            <Icon as={FiFileText} boxSize={8} color={brandRed} />
                            <Heading size="md">Gestión de Expedientes</Heading>
                          </HStack>
                          <Text color="gray.600">
                            Administrar expedientes del sistema con permisos especiales
                          </Text>
                          <Stat mt={2}>
                            <StatNumber color={brandRed}>{conteoExpedientes}</StatNumber>
                            <StatLabel>Total expedientes</StatLabel>
                            <StatHelpText>Última actualización: hoy</StatHelpText>
                          </Stat>
                        </VStack>
                      </Box>
                    </Grid>

                    {/* Sección de gestión de expedientes para administradores */}
                    <Box p={6} bg="red.50" borderRadius="lg" border="1px solid" borderColor="red.200">
                      <VStack spacing={4} align="stretch">
                        <HStack>
                          <Icon as={FiTrash2} color="red.500" boxSize={6} />
                          <VStack align="flex-start" spacing={0}>
                            <Heading size="md" color="red.700">Gestión de Expedientes</Heading>
                            <Text fontSize="sm" color="red.600">
                              Funciones administrativas para expedientes
                            </Text>
                          </VStack>
                        </HStack>
                        
                        <Text fontSize="sm" color="gray.700">
                          Como administrador, puedes eliminar expedientes desde la pestaña de "Denuncias". 
                          Cada expediente tendrá un botón de eliminación visible solo para administradores.
                        </Text>
                        
                        <HStack spacing={4}>
                          <Button 
                            colorScheme="red" 
                            variant="outline"
                            leftIcon={<FiFileText />}
                            onClick={() => {
                              const denunciasTabIndex = 4;
                              setActiveTab(denunciasTabIndex);
                              if (!expedientesLoaded) {
                                loadExpedientes();
                              }
                            }}
                          >
                            Ir a Gestión de Denuncias
                          </Button>
                          
                          <Box p={3} bg="yellow.100" borderRadius="md" border="1px solid" borderColor="yellow.300">
                            <Text fontSize="xs" color="yellow.800" fontWeight="medium">
                              ⚠️ La eliminación de expedientes es irreversible
                            </Text>
                          </Box>
                        </HStack>
                      </VStack>
                    </Box>
                  </VStack>
                </TabPanel>
              )}

              {/* Panel de Estadísticas */}
              <TabPanel p={4}>
                <VStack spacing={6}>
                  <Box textAlign="center" py={6}>
                    <Heading size="lg" mb={4} color={brandRed}>
                      <Icon as={FiFileText} mr={2} />
                      Estadísticas Completas
                    </Heading>
                    <Text color="gray.600" mb={6}>
                      Accede a las estadísticas detalladas de actividad, análisis por períodos, 
                      gráficos interactivos y mapas de zonas de denuncias.
                    </Text>
                    <Button 
                      size="lg" 
                      colorScheme="red" 
                      leftIcon={<FiFileText />}
                      onClick={() => navigate('/estadisticas-actividad')}
                      boxShadow="md"
                      _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                    >
                      Ver Estadísticas Completas
                    </Button>
                  </Box>
                  
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="100%">
                    <Box p={4} bg="blue.50" borderRadius="lg" textAlign="center">
                      <Icon as={FiFileText} boxSize={8} color="blue.500" mb={2} />
                      <Text fontWeight="bold" color="blue.700">Análisis Temporal</Text>
                      <Text fontSize="sm" color="gray.600">
                        Denuncias por semana, día y hora
                      </Text>
                    </Box>
                    
                    <Box p={4} bg="green.50" borderRadius="lg" textAlign="center">
                      <Icon as={FiUsers} boxSize={8} color="green.500" mb={2} />
                      <Text fontWeight="bold" color="green.700">Usuarios Activos</Text>
                      <Text fontSize="sm" color="gray.600">
                        Actividad y participación
                      </Text>
                    </Box>
                    
                    <Box p={4} bg="purple.50" borderRadius="lg" textAlign="center">
                      <Icon as={FiFileText} boxSize={8} color="purple.500" mb={2} />
                      <Text fontWeight="bold" color="purple.700">Tipos de Barreras</Text>
                      <Text fontSize="sm" color="gray.600">
                        Distribución y análisis
                      </Text>
                    </Box>
                  </SimpleGrid>
                  
                  <Box p={4} bg="gray.50" borderRadius="md" w="100%">
                    <Text fontSize="sm" color="gray.600" textAlign="center">
                      <strong>Nota:</strong> Las estadísticas básicas están disponibles en el panel de inicio. 
                      Para análisis detallados, gráficos interactivos y mapas, utiliza el botón anterior.
                    </Text>
                  </Box>
                </VStack>
              </TabPanel>

              {/* Panel de Denuncias */}
              <TabPanel p={4}>
                {!expedientesLoaded ? (
                  <VStack spacing={4}>
                    <Spinner size="xl" color="red.500" />
                    <Text>Cargando expedientes...</Text>
                  </VStack>
                ) : (
                  <Box>
                    {mostrarSinActividad ? (
                      <VStack spacing={6} align="stretch">
                        <HStack justify="space-between" align="center">
                          <Heading size="lg" color={brandRed}>
                            <Icon as={FiAlertCircle} mr={2} />
                            Expedientes Sin Actividad
                          </Heading>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setMostrarSinActividad(false)}
                          >
                            Ver todas las denuncias
                          </Button>
                        </HStack>
                        
                        {expedientesRecientesData && (
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                            {/* Expedientes sin cambios en 7 días */}
                            <Box p={5} bg="orange.50" borderRadius="lg" border="1px solid" borderColor="orange.200">
                              <HStack mb={4}>
                                <Icon as={FiClock} color="orange.500" boxSize={6} />
                                <VStack align="flex-start" spacing={0}>
                                  <Heading size="md" color="orange.700">Sin cambios 7 días</Heading>
                                  <Text fontSize="sm" color="orange.600">
                                    {expedientesRecientesData.sinCambios7Dias.length} expedientes
                                  </Text>
                                </VStack>
                              </HStack>
                              
                              {expedientesRecientesData.sinCambios7Dias.length > 0 ? (
                                <List spacing={2}>
                                  {expedientesRecientesData.sinCambios7Dias.map(exp => (
                                    <ListItem 
                                      key={exp.id} 
                                      p={3} 
                                      bg="white" 
                                      borderRadius="md"
                                      cursor="pointer"
                                      onClick={() => navigate(`/direct-expediente/${exp.id}`)}
                                      _hover={{ bg: "orange.100", transform: "translateY(-1px)" }}
                                      transition="all 0.2s"
                                      boxShadow="sm"
                                    >
                                      <VStack align="stretch" spacing={2}>
                                        <HStack justify="space-between">
                                          <HStack>
                                            <ListIcon as={FiFileText} color="orange.500" />
                                            <Text fontWeight="bold" color="orange.700">{exp.id}</Text>
                                          </HStack>
                                          <Badge colorScheme="orange" size="sm">{exp.estado}</Badge>
                                        </HStack>
                                        <Text fontSize="sm" color="gray.600" noOfLines={2}>
                                          {exp.descripcion}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                          Última actividad: {exp.historial && exp.historial.length > 0 
                                            ? formatearFechaCompleta(exp.historial[exp.historial.length - 1].fecha)
                                            : formatearFechaCompleta(exp.fechaCreacion)}
                                        </Text>
                                      </VStack>
                                    </ListItem>
                                  ))}
                                </List>
                              ) : (
                                <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                                  No hay expedientes sin actividad en 7 días
                                </Text>
                              )}
                            </Box>
                            
                            {/* Expedientes sin cambios en 30 días */}
                            <Box p={5} bg="red.50" borderRadius="lg" border="1px solid" borderColor="red.200">
                              <HStack mb={4}>
                                <Icon as={FiAlertTriangle} color="red.500" boxSize={6} />
                                <VStack align="flex-start" spacing={0}>
                                  <Heading size="md" color="red.700">Sin cambios 30 días</Heading>
                                  <Text fontSize="sm" color="red.600">
                                    {expedientesRecientesData.sinCambios30Dias.length} expedientes críticos
                                  </Text>
                                </VStack>
                              </HStack>
                              
                              {expedientesRecientesData.sinCambios30Dias.length > 0 ? (
                                <List spacing={2}>
                                  {expedientesRecientesData.sinCambios30Dias.map(exp => (
                                    <ListItem 
                                      key={exp.id} 
                                      p={3} 
                                      bg="white" 
                                      borderRadius="md"
                                      cursor="pointer"
                                      borderLeftWidth="4px"
                                      borderLeftColor="red.500"
                                      onClick={() => navigate(`/direct-expediente/${exp.id}`)}
                                      _hover={{ bg: "red.100", transform: "translateY(-1px)" }}
                                      transition="all 0.2s"
                                      boxShadow="sm"
                                    >
                                      <VStack align="stretch" spacing={2}>
                                        <HStack justify="space-between">
                                          <HStack>
                                            <ListIcon as={FiAlertTriangle} color="red.500" />
                                            <Text fontWeight="bold" color="red.700">{exp.id}</Text>
                                          </HStack>
                                          <Badge colorScheme="red" size="sm">{exp.estado}</Badge>
                                        </HStack>
                                        <Text fontSize="sm" color="gray.600" noOfLines={2}>
                                          {exp.descripcion}
                                        </Text>
                                        <Text fontSize="xs" color="red.600" fontWeight="medium">
                                          ⚠️ Última actividad: {exp.historial && exp.historial.length > 0 
                                            ? formatearFechaCompleta(exp.historial[exp.historial.length - 1].fecha)
                                            : formatearFechaCompleta(exp.fechaCreacion)}
                                        </Text>
                                      </VStack>
                                    </ListItem>
                                  ))}
                                </List>
                              ) : (
                                <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                                  No hay expedientes sin actividad en 30 días
                                </Text>
                              )}
                            </Box>
                          </SimpleGrid>
                        )}
                        
                        <Box p={4} bg="gray.50" borderRadius="md">
                          <Text fontSize="sm" color="gray.600" textAlign="center">
                            <strong>Nota:</strong> Los expedientes sin actividad requieren atención prioritaria. 
                            Haz clic en cualquier expediente para ver sus detalles y realizar acciones.
                          </Text>
                        </Box>
                      </VStack>
                    ) : (
                      <VStack spacing={6} align="stretch">
                        <VStack align="stretch" spacing={2}>
                          <HStack justify="space-between" align="center">
                            <Heading size="lg" color={brandRed}>Denuncias Presentadas</Heading>
                            <HStack spacing={4}>
                              <Text fontWeight="medium">Total: {conteoExpedientes} expedientes</Text>
                              {isAdmin && expedientesSeleccionados.size > 0 && (
                                <Button
                                  colorScheme="red"
                                  leftIcon={<FiTrash2 />}
                                  onClick={() => {
                                    setExpedienteToDelete('MULTIPLE');
                                    onOpen();
                                  }}
                                  isLoading={eliminandoMultiples}
                                  size="sm"
                                >
                                  Eliminar {expedientesSeleccionados.size} seleccionados
                                </Button>
                              )}
                              <Button colorScheme="green" leftIcon={<FiDownload />} onClick={exportarDenunciasExcel} isLoading={exportandoExcel}>
                                Exportar a Excel
                              </Button>
                            </HStack>
                          </HStack>
                          {exportandoExcel && (
                            <Box w="full" maxW="sm">
                              <Text fontSize="sm" color="gray.600" mb={1}>Preparando exportación...</Text>
                              <Progress value={exportProgressPercent} size="sm" colorScheme="green" borderRadius="full" hasStripe />
                            </Box>
                          )}
                        </VStack>
                        
                        {/* Barra de selección múltiple para administradores */}
                        {isAdmin && paginationData.currentItems.length > 0 && (
                          <Box p={3} bg="blue.50" borderRadius="lg" border="1px solid" borderColor="blue.200">
                            <HStack justify="space-between" align="center">
                              <HStack spacing={3}>
                                <Checkbox
                                  isChecked={expedientesSeleccionados.size === paginationData.currentItems.length && paginationData.currentItems.length > 0}
                                  isIndeterminate={expedientesSeleccionados.size > 0 && expedientesSeleccionados.size < paginationData.currentItems.length}
                                  onChange={seleccionarTodos}
                                  colorScheme="blue"
                                >
                                  <Text fontSize="sm" fontWeight="medium">
                                    Seleccionar todos ({paginationData.currentItems.length})
                                  </Text>
                                </Checkbox>
                                {expedientesSeleccionados.size > 0 && (
                                  <Badge colorScheme="blue" variant="solid" px={2} py={1}>
                                    {expedientesSeleccionados.size} seleccionados
                                  </Badge>
                                )}
                              </HStack>
                              
                              {expedientesSeleccionados.size > 0 && (
                                <HStack spacing={2}>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    colorScheme="gray"
                                    onClick={limpiarSeleccion}
                                  >
                                    Limpiar selección
                                  </Button>
                                  <Button
                                    size="sm"
                                    colorScheme="red"
                                    leftIcon={<FiTrash2 />}
                                    onClick={() => {
                                      setExpedienteToDelete('MULTIPLE');
                                      onOpen();
                                    }}
                                    isLoading={eliminandoMultiples}
                                  >
                                    Eliminar {expedientesSeleccionados.size}
                                  </Button>
                                </HStack>
                              )}
                            </HStack>
                          </Box>
                        )}
                        
                        {/* Filtros */}
                        <Box p={4} bg="gray.50" borderRadius="lg" mb={4}>
                          <VStack spacing={4} align="stretch">
                            {/* Primera fila de filtros */}
                            <HStack spacing={4} wrap="wrap">
                              <InputGroup maxW="300px">
                                <InputLeftElement>
                                  <Icon as={FiSearch} color="gray.400" />
                                </InputLeftElement>
                                <Input
                                  placeholder="Buscar por ID, email o descripción..."
                                  value={filtros.busqueda}
                                  onChange={(e) => setFiltros(prev => ({ ...prev, busqueda: e.target.value }))}
                                  bg="white"
                                />
                              </InputGroup>
                              
                              <Select
                                placeholder="Filtrar por estado"
                                value={filtros.estado}
                                onChange={(e) => setFiltros(prev => ({ ...prev, estado: e.target.value }))}
                                maxW="200px"
                                bg="white"
                              >
                                <option value="En trámite">En trámite</option>
                                <option value="Resuelto">Resuelto</option>
                                <option value="Rechazada">Rechazada</option>
                              </Select>
                              
                              <Select
                                placeholder="Filtrar por tipo de barrera"
                                value={filtros.tipoBarrera}
                                onChange={(e) => setFiltros(prev => ({ ...prev, tipoBarrera: e.target.value }))}
                                maxW="250px"
                                bg="white"
                              >
                                {Object.keys(estadisticas.tiposBarrera).map(tipo => (
                                  <option key={tipo} value={tipo}>{tipo}</option>
                                ))}
                              </Select>
                              
                              <Select
                                placeholder="Filtrar por departamento"
                                value={filtros.departamento}
                                onChange={(e) => setFiltros(prev => ({ ...prev, departamento: e.target.value }))}
                                maxW="200px"
                                bg="white"
                              >
                                {Object.keys(estadisticas.departamentos).map(dept => (
                                  <option key={dept} value={dept}>{dept}</option>
                                ))}
                              </Select>
                              
                              <InputGroup maxW="300px">
                                <InputLeftElement>
                                  <Icon as={FiSearch} color="gray.400" />
                                </InputLeftElement>
                                <Input
                                  placeholder="Buscar por dirección, localidad..."
                                  value={filtros.direccion}
                                  onChange={(e) => setFiltros(prev => ({ ...prev, direccion: e.target.value }))}
                                  bg="white"
                                />
                              </InputGroup>
                              
                              <InputGroup maxW="300px">
                                <InputLeftElement>
                                  <Icon as={FiSearch} color="gray.400" />
                                </InputLeftElement>
                                <Input
                                  placeholder="Buscar en comunicaciones internas..."
                                  value={filtros.comunicacionInterna}
                                  onChange={(e) => setFiltros(prev => ({ ...prev, comunicacionInterna: e.target.value }))}
                                  bg="white"
                                />
                              </InputGroup>
                            </HStack>

                            {/* Segunda fila: Filtros de fecha y ordenamiento */}
                            <HStack spacing={4} wrap="wrap">
                              <VStack align="flex-start" spacing={1}>
                                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                  <Icon as={FiCalendar} mr={1} />
                                  Desde:
                                </Text>
                                <Input
                                  type="date"
                                  value={filtros.fechaDesde}
                                  onChange={(e) => setFiltros(prev => ({ ...prev, fechaDesde: e.target.value }))}
                                  maxW="180px"
                                  bg="white"
                                  size="sm"
                                />
                              </VStack>
                              
                              <VStack align="flex-start" spacing={1}>
                                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                  Hasta:
                                </Text>
                                <Input
                                  type="date"
                                  value={filtros.fechaHasta}
                                  onChange={(e) => setFiltros(prev => ({ ...prev, fechaHasta: e.target.value }))}
                                  maxW="180px"
                                  bg="white"
                                  size="sm"
                                />
                              </VStack>
                              
                              <VStack align="flex-start" spacing={1}>
                                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                  <Icon as={FiEdit3} mr={1} />
                                  Últ. modif. desde:
                                </Text>
                                <Input
                                  type="date"
                                  value={filtros.ultimaModificacionDesde}
                                  onChange={(e) => setFiltros(prev => ({ ...prev, ultimaModificacionDesde: e.target.value }))}
                                  maxW="180px"
                                  bg="white"
                                  size="sm"
                                />
                              </VStack>
                              
                              <VStack align="flex-start" spacing={1}>
                                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                  Últ. modif. hasta:
                                </Text>
                                <Input
                                  type="date"
                                  value={filtros.ultimaModificacionHasta}
                                  onChange={(e) => setFiltros(prev => ({ ...prev, ultimaModificacionHasta: e.target.value }))}
                                  maxW="180px"
                                  bg="white"
                                  size="sm"
                                />
                              </VStack>
                              
                              <VStack align="flex-start" spacing={1}>
                                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                  Ordenar por:
                                </Text>
                                <Select
                                  value={filtros.ordenarPor}
                                  onChange={(e) => setFiltros(prev => ({ ...prev, ordenarPor: e.target.value }))}
                                  maxW="200px"
                                  bg="white"
                                  size="sm"
                                >
                                  <option value="fecha_desc">Fecha (más reciente)</option>
                                  <option value="fecha_asc">Fecha (más antigua)</option>
                                  <option value="id_asc">ID (A-Z)</option>
                                  <option value="id_desc">ID (Z-A)</option>
                                </Select>
                              </VStack>
                              
                              {(filtros.busqueda || filtros.estado || filtros.tipoBarrera || filtros.departamento || filtros.fechaDesde || filtros.fechaHasta || filtros.ultimaModificacionDesde || filtros.ultimaModificacionHasta || filtros.direccion || filtros.comunicacionInterna) && (
                                <VStack align="flex-start" spacing={1}>
                                  <Text fontSize="sm" color="transparent">Limpiar</Text>
                                  <IconButton
                                    aria-label="Limpiar filtros"
                                    icon={<FiX />}
                                    onClick={() => {
                                      setFiltros({
                                        busqueda: '',
                                        estado: '',
                                        tipoBarrera: '',
                                        departamento: '',
                                        fechaDesde: '',
                                        fechaHasta: '',
                                        ultimaModificacionDesde: '',
                                        ultimaModificacionHasta: '',
                                        direccion: '',
                                        comunicacionInterna: '',
                                        ordenarPor: 'fecha_desc'
                                      });
                                      setBusquedaDebounce('');
                                    }}
                                    variant="outline"
                                    colorScheme="red"
                                    size="sm"
                                  />
                                </VStack>
                              )}
                            </HStack>
                          </VStack>
                          
                          <HStack justify="space-between" mt={3}>
                            <Text fontSize="sm" color="gray.600">
                              Mostrando {paginationData.startIndex}-{paginationData.endIndex} de {paginationData.totalItems} expedientes
                            </Text>
                            <HStack>
                              <Icon as={FiFilter} color="gray.500" />
                              <Text fontSize="sm" color="gray.500">
                                {filteredExpedientes.length !== expedientes.length ? 'Filtrado' : 'Sin filtros'}
                              </Text>
                            </HStack>
                          </HStack>
                        </Box>

                        {loadingExpedientes ? (
                          <VStack spacing={6} py={20}>
                            <Spinner size="xl" color="blue.500" thickness="4px" speed="0.65s" />
                            <VStack spacing={2}>
                              <Text fontSize="lg" color="blue.600" fontWeight="medium">
                                Cargando expedientes...
                              </Text>
                              <Text fontSize="sm" color="gray.500">
                                Por favor espera un momento
                              </Text>
                            </VStack>
                          </VStack>
                        ) : paginationData.currentItems.length > 0 ? (
                          <>
                            <Box overflowX="auto">
                              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                                {paginationData.currentItems.map(exp => (
                                <Box
                                  key={exp.id}
                                  p={4}
                                  bg="white"
                                  borderRadius="lg"
                                  border="1px solid"
                                  borderColor={expedientesSeleccionados.has(exp.id) ? "blue.300" : "gray.200"}
                                  position="relative"
                                  transition="all 0.2s"
                                  boxShadow={expedientesSeleccionados.has(exp.id) ? "md" : "sm"}
                                  _hover={{
                                    bg: "gray.50",
                                    transform: "translateY(-2px)",
                                    boxShadow: "md",
                                    borderColor: expedientesSeleccionados.has(exp.id) ? "blue.400" : "red.300"
                                  }}
                                >
                                  <VStack align="stretch" spacing={3}>
                                    <HStack justify="space-between" align="flex-start">
                                      <HStack spacing={3} flex="1">
                                        {isAdmin && (
                                          <Checkbox
                                            isChecked={expedientesSeleccionados.has(exp.id)}
                                            onChange={() => toggleSeleccionExpediente(exp.id)}
                                            colorScheme="blue"
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        )}
                                        <VStack align="flex-start" spacing={1} flex="1">
                                          <HStack>
                                            <Icon as={FiFileText} color={brandRed} boxSize={4} />
                                            <Text
                                              fontWeight="bold"
                                              color={brandRed}
                                              fontSize="lg"
                                              cursor="pointer"
                                              onClick={() => navigate(`/direct-expediente/${exp.id}`)}
                                              _hover={{ textDecoration: "underline" }}
                                            >
                                              {exp.id}
                                            </Text>
                                          </HStack>
                                          <Text fontSize="sm" color="gray.500">
                                            {formatearFechaCompleta(exp.fechaCreacion)}
                                          </Text>
                                        </VStack>
                                      </HStack>
                                      <VStack spacing={2} align="flex-end">
                                        <HStack spacing={2}>
                                          <Badge
                                            colorScheme={
                                              exp.estado === 'En trámite' ? 'orange' :
                                              exp.estado === 'Resuelto' ? 'green' :
                                              exp.estado === 'Rechazada' ? 'red' : 'gray'
                                            }
                                            variant="solid"
                                            fontSize="xs"
                                          >
                                            {exp.estado}
                                          </Badge>
                                          {isAdmin && (
                                            <IconButton
                                              aria-label="Eliminar expediente"
                                              icon={<FiTrash2 />}
                                              size="sm"
                                              colorScheme="red"
                                              variant="ghost"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setExpedienteToDelete(exp.id);
                                                onOpen();
                                              }}
                                              _hover={{ bg: "red.100" }}
                                            />
                                          )}
                                        </HStack>
                                      </VStack>
                                    </HStack>
                                    
                                    <Box 
                                      cursor="pointer"
                                      onClick={() => navigate(`/direct-expediente/${exp.id}`)}
                                    >
                                      <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={1}>
                                        {exp.tipoBarrera}
                                      </Text>
                                      <Text fontSize="sm" color="gray.600" noOfLines={2}>
                                        {exp.descripcion}
                                      </Text>
                                    </Box>
                                    
                                    <HStack 
                                      justify="space-between" 
                                      fontSize="xs" 
                                      color="gray.500"
                                      cursor="pointer"
                                      onClick={() => navigate(`/direct-expediente/${exp.id}`)}
                                    >
                                      <Text>{exp.Departamento}</Text>
                                      <Text>{exp.email}</Text>
                                    </HStack>
                                    
                                    {exp.historial && exp.historial.length > 0 && (
                                      <Box 
                                        pt={2} 
                                        borderTop="1px solid" 
                                        borderColor="gray.100"
                                        cursor="pointer"
                                        onClick={() => navigate(`/direct-expediente/${exp.id}`)}
                                      >
                                        <Text fontSize="xs" color="gray.500">
                                          Última actualización: {formatearFechaCompleta(exp.historial[exp.historial.length - 1].fecha)}
                                        </Text>
                                      </Box>
                                    )}
                                  </VStack>
                                </Box>
                                ))}
                              </SimpleGrid>
                            </Box>
                            
                            {/* Paginación */}
                            {paginationData.totalPages > 1 && (
                              <HStack justify="center" spacing={2} mt={6}>
                                <IconButton
                                  aria-label="Página anterior"
                                  icon={<FiChevronLeft />}
                                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                  isDisabled={currentPage === 1}
                                  variant="outline"
                                />
                                
                                <HStack spacing={1}>
                                  {Array.from({ length: Math.min(5, paginationData.totalPages) }, (_, i) => {
                                    let pageNumber;
                                    if (paginationData.totalPages <= 5) {
                                      pageNumber = i + 1;
                                    } else if (currentPage <= 3) {
                                      pageNumber = i + 1;
                                    } else if (currentPage >= paginationData.totalPages - 2) {
                                      pageNumber = paginationData.totalPages - 4 + i;
                                    } else {
                                      pageNumber = currentPage - 2 + i;
                                    }
                                    
                                    return (
                                      <Button
                                        key={pageNumber}
                                        size="sm"
                                        variant={currentPage === pageNumber ? "solid" : "outline"}
                                        colorScheme={currentPage === pageNumber ? "red" : "gray"}
                                        onClick={() => setCurrentPage(pageNumber)}
                                      >
                                        {pageNumber}
                                      </Button>
                                    );
                                  })}
                                </HStack>
                                
                                <IconButton
                                  aria-label="Página siguiente"
                                  icon={<FiChevronRight />}
                                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, paginationData.totalPages))}
                                  isDisabled={currentPage === paginationData.totalPages}
                                  variant="outline"
                                />
                              </HStack>
                            )}
                          </>
                        ) : (
                          <Box textAlign="center" py={10}>
                            <Icon as={FiFileText} boxSize={12} color="gray.300" mb={4} />
                            <Text fontSize="lg" color="gray.500" mb={2}>No hay expedientes que coincidan con los filtros</Text>
                            <Text fontSize="sm" color="gray.400">
                              Intenta ajustar los filtros para ver más resultados.
                            </Text>
                          </Box>
                        )}
                      </VStack>
                    )}
                  </Box>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Container>

      {/* Diálogo de confirmación para eliminar */}
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {expedienteToDelete === 'MULTIPLE'
                ? `Eliminar ${expedientesSeleccionados.size} Denuncias`
                : 'Eliminar Denuncia'
              }
            </AlertDialogHeader>
            <AlertDialogBody>
              {expedienteToDelete === 'MULTIPLE' ? (
                <VStack spacing={3} align="stretch">
                  <Text>
                    ¿Estás seguro de que quieres eliminar {expedientesSeleccionados.size} expedientes seleccionados?
                  </Text>
                  <Text fontSize="sm" color="red.600" fontWeight="medium">
                    ⚠️ Esta acción no se puede deshacer y eliminará permanentemente:
                  </Text>
                  <Box maxH="200px" overflowY="auto" p={3} bg="gray.50" borderRadius="md">
                    <VStack spacing={1} align="stretch">
                      {Array.from(expedientesSeleccionados).map(id => (
                        <Text key={id} fontSize="sm" fontFamily="mono">
                          • {id}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                </VStack>
              ) : (
                <Text>
                  ¿Estás seguro? Esta acción no se puede deshacer.
                </Text>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={onClose}
                isDisabled={eliminandoExpediente || eliminandoMultiples}
              >
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                onClick={expedienteToDelete === 'MULTIPLE' ? eliminarExpedientesSeleccionados : handleDeleteExpediente}
                ml={3}
                isLoading={eliminandoExpediente || eliminandoMultiples}
                loadingText={expedienteToDelete === 'MULTIPLE' ? "Eliminando múltiples..." : "Eliminando..."}
              >
                {expedienteToDelete === 'MULTIPLE'
                  ? `Eliminar ${expedientesSeleccionados.size} expedientes`
                  : 'Eliminar'
                }
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};
